// MA-C20 — `two_color_ten_frame` renderer for ms.math.06 make-ten items
// ("What part is missing?" / "Drag the missing part to the frame.").
//
// THE GAP THIS CLOSES: these 30 foundation items rendered BLANK — there was
// no ten-frame renderer that fills only a PART of the frame in a named
// color, leaving the rest open for the child to figure out the missing part.
//
// ANSWER-SAFE by construction: the mapper (contentToEngineItem.ts) only ever
// emits this kind when the filled count (`a`, the authored shownPart) is NOT
// the answer (the answer is the missing/open part) — this component just
// draws whatever `a`-of-`total` it is given. The aria-label speaks the
// filled + total counts (exactly what a sighted child perceives by looking),
// mirroring the established `TwoTenFramesMissingAddend` precedent — never
// the open/missing count, which would speak the answer.
//
// ADDITIVE — registered in rendererRegistry.tsx beside the other built-ins.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";

const SHOWN_COLOR_MAP: Record<string, string> = {
  purple: "var(--pal-purple, #a98ac9)",
  orange: "var(--pal-orange, #e89b6b)",
  teal: "var(--pal-teal, #4f9d95)",
  red: "var(--pal-red, #c0574a)",
  green: "var(--pal-green, #88b591)",
  blue: "var(--pal-blue, #6e9ccf)",
};
// Fail-open: an unrecognized/absent authored color never throws or blanks
// the cell — it falls back to this neutral fill.
const FALLBACK_FILL = "var(--color-border-secondary, #cdbfae)";

const CELL = 68;
const PITCH = 70; // CELL + 2px gap
const COLS = 5;
const PAD = 6;

export default function TwoColorTenFrameRenderer({ a, total, color }: MathRendererProps): ReactElement {
  const frameSize = Number.isFinite(total) && (total as number) > 0 ? Math.floor(total as number) : 10;
  const filled = Math.max(0, Math.min(frameSize, Math.floor(a ?? 0)));
  const fill = (typeof color === "string" && SHOWN_COLOR_MAP[color]) || FALLBACK_FILL;
  const rows = Math.ceil(frameSize / COLS);
  const W = COLS * PITCH + 2 * PAD;
  const H = rows * PITCH + 2 * PAD;
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => PAD + Math.floor(i / COLS) * PITCH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img"
      // Speaks the same visual facts a sighted child perceives (filled +
      // total cell counts) — never the open/missing count, which is the
      // answer. Mirrors TwoTenFramesMissingAddend's established pattern.
      aria-label={`Ten frame. ${filled} filled. ${frameSize} total.`}>
      {Array.from({ length: frameSize }).map((_, i) => {
        const x = cellX(i);
        const y = cellY(i);
        const isFilled = i < filled;
        return (
          <g key={i}>
            <rect x={x} y={y} width={CELL} height={CELL} rx="8"
              fill="none" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {isFilled && (
              <circle cx={x + CELL / 2} cy={y + CELL / 2} r="22" fill={fill} />
            )}
          </g>
        );
      })}
    </svg>
  );
}
